/**
 * Security alerts — the admin's view of @hub/security.
 *
 * Three things live here: which alerts email and at what threshold, where that
 * mail goes, and what has actually happened. The alert list renders entirely
 * from the catalog the API returns, so a new alert in the package shows up here
 * without a change in this file.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AlertCategory,
  AlertDefinition,
  AlertKey,
  AlertSetting,
  DeliveryMode,
  DeliverySettingsPublic,
  FirewallSettings,
  FirewallStatus,
  TrustProxyInfo,
  SecurityBlock,
  SecurityEvent,
  SecuritySettingsResponse,
  SecurityTestResponse,
} from '@hub/shared';
import { api } from '../../api/client';
import {
  ActionButton, Alert, Card, Loading, Pill, SectionTitle, fmtAgo, fmtDate, useFlash,
} from '../../components/ui';

const CATEGORY_LABEL: Record<AlertCategory, string> = {
  blocking: 'Blocking and attacks',
  access: 'Access and privilege',
  accounts: 'Account changes',
  resources: 'Resources',
  integrity: 'File integrity',
};

const CATEGORY_ORDER: AlertCategory[] = ['blocking', 'access', 'accounts', 'resources', 'integrity'];

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function severityTone(severity: string): 'muted' | 'emerald' | 'red' {
  return severity === 'critical' ? 'red' : 'muted';
}

export function AdminSecurityPage() {
  const [catalog, setCatalog] = useState<AlertDefinition[]>([]);
  const [alerts, setAlerts] = useState<AlertSetting[]>([]);
  const [delivery, setDelivery] = useState<DeliverySettingsPublic | null>(null);
  const [firewall, setFirewall] = useState<FirewallSettings | null>(null);
  const [firewallStatus, setFirewallStatus] = useState<FirewallStatus | null>(null);
  const [trustProxy, setTrustProxy] = useState<TrustProxyInfo | null>(null);
  const [password, setPassword] = useState('');
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [blocks, setBlocks] = useState<SecurityBlock[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { show: showFlash, node: flashNode } = useFlash();

  const applySettings = useCallback((r: SecuritySettingsResponse) => {
    setCatalog(r.catalog);
    setAlerts(r.alerts);
    setDelivery(r.delivery);
    setFirewall(r.firewall);
    setFirewallStatus(r.firewallStatus);
    setTrustProxy(r.trustProxy);
    // The API never returns the stored password, so the field always starts
    // empty and an empty save means "leave it alone".
    setPassword('');
  }, []);

  const load = useCallback(async () => {
    try {
      const [settings, ev, bl] = await Promise.all([
        api.get<SecuritySettingsResponse>('/api/admin/security/settings'),
        api.get<{ events: SecurityEvent[] }>('/api/admin/security/events?limit=50'),
        api.get<{ blocks: SecurityBlock[] }>('/api/admin/security/blocks'),
      ]);
      applySettings(settings);
      setEvents(ev.events);
      setBlocks(bl.blocks);
      setLoadError(null);
    } catch (err) {
      setLoadError(errText(err));
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => { void load(); }, [load]);

  const settingFor = useCallback(
    (key: AlertKey) => alerts.find((a) => a.key === key),
    [alerts],
  );

  const updateAlert = useCallback((key: AlertKey, patch: Partial<AlertSetting>) => {
    setAlerts((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  }, []);

  const saveAlerts = useCallback(async () => {
    const r = await api.put<SecuritySettingsResponse>('/api/admin/security/settings', { alerts });
    applySettings(r);
    showFlash('Alert settings saved', 'ok');
  }, [alerts, applySettings, showFlash]);

  const saveDelivery = useCallback(async () => {
    if (!delivery) return;
    const r = await api.put<SecuritySettingsResponse>('/api/admin/security/delivery', {
      mode: delivery.mode,
      alertTo: delivery.alertTo,
      alertFrom: delivery.alertFrom,
      throttleMinutes: delivery.throttleMinutes,
      smtp: { ...delivery.smtp, ...(password ? { password } : {}) },
    });
    applySettings(r);
    showFlash('Delivery settings saved', 'ok');
  }, [delivery, password, applySettings, showFlash]);

  const sendTest = useCallback(async () => {
    const r = await api.post<SecurityTestResponse>('/api/admin/security/test');
    showFlash(
      r.previewUrl ? `Test sent to ${r.sentTo} — preview: ${r.previewUrl}` : `Test sent to ${r.sentTo}`,
      'ok',
    );
  }, [showFlash]);

  const saveFirewall = useCallback(async () => {
    if (!firewall) return;
    const r = await api.put<SecuritySettingsResponse>('/api/admin/security/firewall', {
      enabled: firewall.enabled,
      scope: firewall.scope,
      removeOnUnblock: firewall.removeOnUnblock,
    });
    applySettings(r);
    showFlash('Firewall settings saved', 'ok');
  }, [firewall, applySettings, showFlash]);

  const syncFirewall = useCallback(async () => {
    const r = await api.post<{ synced: number; failed: number }>('/api/admin/security/firewall/sync');
    showFlash(
      r.failed > 0
        ? `${r.synced} rule(s) ensured, ${r.failed} failed — check that ufw is reachable`
        : `${r.synced} rule(s) ensured in ufw`,
      r.failed > 0 ? 'warn' : 'ok',
    );
    await load();
  }, [showFlash, load]);

  const unblock = useCallback(async (ip: string) => {
    const r = await api.delete<{ firewallError?: string }>(
      `/api/admin/security/blocks/${encodeURIComponent(ip)}`,
    );
    setBlocks((prev) => prev.filter((b) => b.ip !== ip));
    // The hub-level block is gone either way; say so if a firewall rule outlived it.
    showFlash(
      r.firewallError ? `${ip} unblocked, but its ufw rule remains: ${r.firewallError}` : `${ip} unblocked`,
      r.firewallError ? 'warn' : 'ok',
    );
  }, [showFlash]);

  const byCategory = useMemo(() => {
    const groups = new Map<AlertCategory, AlertDefinition[]>();
    for (const def of catalog) {
      const list = groups.get(def.category) ?? [];
      list.push(def);
      groups.set(def.category, list);
    }
    return CATEGORY_ORDER
      .map((category) => ({ category, defs: groups.get(category) ?? [] }))
      .filter((g) => g.defs.length > 0);
  }, [catalog]);

  const enabledCount = alerts.filter((a) => a.enabled && a.level !== 0).length;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ color: 'var(--ha-text-primary)', fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Security
          </h2>
          {catalog.length > 0 && <Pill tone="muted">{enabledCount} of {catalog.length} alerts on</Pill>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            className="btn-ghost"
            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
            onClick={() => { void load(); }}
          >
            Refresh
          </button>
          {flashNode}
        </div>
      </div>

      <p className="muted" style={{ fontSize: '0.8rem', marginBottom: '1.25rem' }}>
        CSF/LFD-style notifications. Each alert keeps its ConfigServer name, so a threshold you already
        know from <span className="mono">csf.conf</span> means the same thing here. Blocks are applied by
        the hub itself and do not touch the host firewall.
      </p>

      {loadError && (
        <div style={{ marginBottom: '1rem' }}>
          <Alert tone="error">
            <span>{loadError}</span>
            <button
              type="button"
              className="btn-ghost"
              style={{ marginLeft: '0.75rem', padding: '0.15rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => { void load(); }}
            >
              Retry
            </button>
          </Alert>
        </div>
      )}

      {loading && <Loading />}

      {delivery && (
        <DeliveryCard
          delivery={delivery}
          password={password}
          onChange={setDelivery}
          onPasswordChange={setPassword}
          onSave={saveDelivery}
          onTest={sendTest}
        />
      )}

      {byCategory.length > 0 && (
        <Card style={{ marginBottom: '1rem' }}>
          <SectionTitle
            actions={<ActionButton busyLabel="Saving…" onClick={saveAlerts}>Save alerts</ActionButton>}
          >
            Alerts
          </SectionTitle>

          {byCategory.map(({ category, defs }) => (
            <div key={category} style={{ marginBottom: '1.5rem' }}>
              <div className="muted" style={{
                fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                marginBottom: '0.5rem',
              }}>
                {CATEGORY_LABEL[category]}
              </div>
              {defs.map((def) => {
                const setting = settingFor(def.key);
                if (!setting) return null;
                return (
                  <AlertRow
                    key={def.key}
                    def={def}
                    setting={setting}
                    onChange={(patch) => updateAlert(def.key, patch)}
                  />
                );
              })}
            </div>
          ))}
        </Card>
      )}

      {firewall && firewallStatus && (
        <FirewallCard
          firewall={firewall}
          status={firewallStatus}
          onChange={setFirewall}
          onSave={saveFirewall}
          onSync={syncFirewall}
        />
      )}

      {trustProxy && <TrustProxyCard info={trustProxy} />}

      <BlocksCard blocks={blocks} onUnblock={unblock} />
      <EventsCard events={events} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delivery
// ---------------------------------------------------------------------------

function DeliveryCard({ delivery, password, onChange, onPasswordChange, onSave, onTest }: {
  delivery: DeliverySettingsPublic;
  password: string;
  onChange: (next: DeliverySettingsPublic) => void;
  onPasswordChange: (next: string) => void;
  onSave: () => Promise<unknown>;
  onTest: () => Promise<unknown>;
}) {
  const set = <K extends keyof DeliverySettingsPublic>(key: K, value: DeliverySettingsPublic[K]) =>
    onChange({ ...delivery, [key]: value });

  const setSmtp = (patch: Partial<DeliverySettingsPublic['smtp']>) =>
    onChange({ ...delivery, smtp: { ...delivery.smtp, ...patch } });

  return (
    <Card style={{ marginBottom: '1rem' }}>
      <SectionTitle
        actions={
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <ActionButton busyLabel="Sending…" onClick={onTest}>Send test email</ActionButton>
            <ActionButton busyLabel="Saving…" onClick={onSave}>Save delivery</ActionButton>
          </div>
        }
      >
        Delivery
      </SectionTitle>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <ModeOption
          mode="library"
          current={delivery.mode}
          title="Use the hub mailer"
          description="Security alerts go out on the same transport as verification mail. Nothing else to configure."
          onSelect={(mode) => set('mode', mode)}
        />
        <ModeOption
          mode="custom_smtp"
          current={delivery.mode}
          title="Use a custom SMTP server"
          description="A dedicated server for security mail only, so alerts do not share fate with product mail."
          onSelect={(mode) => set('mode', mode)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))', gap: '0.75rem' }}>
        <Field
          label="Send alerts to"
          help={`CSF's LF_ALERT_TO. Blank sends to ${delivery.effectiveAlertTo}.`}
        >
          <input
            className="form-input"
            type="email"
            placeholder={delivery.effectiveAlertTo}
            value={delivery.alertTo}
            onChange={(e) => set('alertTo', e.target.value)}
          />
        </Field>

        <Field label="Send alerts from" help="CSF's LF_ALERT_FROM. Blank uses the transport's own sender.">
          <input
            className="form-input"
            type="email"
            placeholder="csf@example.com"
            value={delivery.alertFrom}
            onChange={(e) => set('alertFrom', e.target.value)}
          />
        </Field>

        <Field
          label="Repeat suppression"
          help="Minutes an identical alert is held back. Events are still recorded. 0 disables it."
        >
          <input
            className="form-input"
            type="number"
            min={0}
            max={1440}
            value={delivery.throttleMinutes}
            onChange={(e) => set('throttleMinutes', Number(e.target.value))}
          />
        </Field>
      </div>

      {delivery.mode === 'custom_smtp' && (
        <div style={{
          marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--ha-border)',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', gap: '0.75rem',
        }}>
          <Field label="SMTP host">
            <input
              className="form-input"
              value={delivery.smtp.host}
              placeholder="smtp.example.com"
              onChange={(e) => setSmtp({ host: e.target.value })}
            />
          </Field>
          <Field label="Port">
            <input
              className="form-input"
              type="number"
              min={1}
              max={65535}
              value={delivery.smtp.port}
              onChange={(e) => setSmtp({ port: Number(e.target.value) })}
            />
          </Field>
          <Field label="Username" help="Leave blank for a relay that needs no credentials.">
            <input
              className="form-input"
              value={delivery.smtp.user}
              onChange={(e) => setSmtp({ user: e.target.value })}
            />
          </Field>
          <Field
            label="Password"
            help={delivery.passwordSet ? 'A password is stored. Leave blank to keep it.' : 'No password stored.'}
          >
            <input
              className="form-input"
              type="password"
              autoComplete="new-password"
              placeholder={delivery.passwordSet ? '••••••••' : ''}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
            />
          </Field>
          <Field label="Implicit TLS" help="On for port 465. Off negotiates STARTTLS.">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={delivery.smtp.secure}
                onChange={(e) => setSmtp({ secure: e.target.checked })}
              />
              <span className="muted">Connect over TLS directly</span>
            </label>
          </Field>
        </div>
      )}
    </Card>
  );
}

function ModeOption({ mode, current, title, description, onSelect }: {
  mode: DeliveryMode;
  current: DeliveryMode;
  title: string;
  description: string;
  onSelect: (mode: DeliveryMode) => void;
}) {
  const active = current === mode;
  return (
    <label style={{
      flex: '1 1 18rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
      padding: '0.75rem', borderRadius: '0.375rem', cursor: 'pointer',
      background: 'var(--ha-bg-elevated)',
      border: `1px solid ${active ? 'var(--ha-accent-blue)' : 'var(--ha-border)'}`,
    }}>
      <input
        type="radio"
        name="delivery-mode"
        checked={active}
        onChange={() => onSelect(mode)}
        style={{ marginTop: '0.2rem' }}
      />
      <span>
        <span style={{ color: 'var(--ha-text-primary)', fontSize: '0.9rem', display: 'block' }}>{title}</span>
        <span className="muted" style={{ fontSize: '0.78rem' }}>{description}</span>
      </span>
    </label>
  );
}

function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="form-label">{label}</label>
      {children}
      {help && <div className="muted" style={{ fontSize: '0.72rem', marginTop: '0.25rem' }}>{help}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

function AlertRow({ def, setting, onChange }: {
  def: AlertDefinition;
  setting: AlertSetting;
  onChange: (patch: Partial<AlertSetting>) => void;
}) {
  const off = !setting.enabled;

  return (
    <div style={{
      padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.375rem',
      background: 'var(--ha-bg-elevated)', opacity: off ? 0.65 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', flexWrap: 'wrap' }}>
        <input
          type="checkbox"
          checked={setting.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          style={{ marginTop: '0.25rem' }}
        />
        <div style={{ flex: '1 1 20rem', minWidth: '15rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong style={{ color: 'var(--ha-text-primary)', fontSize: '0.9rem' }}>{def.label}</strong>
            <span className="mono muted" style={{ fontSize: '0.72rem' }}>{def.key}</span>
            <Pill tone={severityTone(def.severity)}>{def.severity}</Pill>
          </div>
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>{def.hubTrigger}</div>
          <div className="muted" style={{ fontSize: '0.72rem', marginTop: '0.15rem', fontStyle: 'italic' }}>
            CSF: {def.csfMeaning}
          </div>
          {def.note && (
            <div style={{ fontSize: '0.72rem', marginTop: '0.35rem', color: 'var(--ha-warn)' }}>{def.note}</div>
          )}
        </div>
      </div>

      {(def.levels || def.params.length > 0) && setting.enabled && (
        <div style={{
          display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.75rem',
          paddingLeft: '1.5rem',
        }}>
          {def.levels && (
            <div style={{ minWidth: '16rem' }}>
              <label className="form-label">Level</label>
              <select
                className="form-input"
                value={setting.level ?? def.defaultLevel ?? 0}
                onChange={(e) => onChange({ level: Number(e.target.value) })}
              >
                {def.levels.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
          )}
          {def.params.map((p) => (
            <div key={p.name} style={{ width: '11rem' }}>
              <label className="form-label">{p.label}</label>
              <input
                className="form-input"
                type="number"
                min={p.min}
                max={p.max}
                step={p.step ?? 1}
                value={setting.params[p.name] ?? p.default}
                onChange={(e) => onChange({
                  params: { ...setting.params, [p.name]: Number(e.target.value) },
                })}
              />
              <div className="muted" style={{ fontSize: '0.7rem', marginTop: '0.2rem' }}>
                {p.help ?? p.unit}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Firewall
// ---------------------------------------------------------------------------

function FirewallCard({ firewall, status, onChange, onSave, onSync }: {
  firewall: FirewallSettings;
  status: FirewallStatus;
  onChange: (next: FirewallSettings) => void;
  onSave: () => Promise<unknown>;
  onSync: () => Promise<unknown>;
}) {
  const healthy = status.available && status.active;

  return (
    <Card style={{ marginBottom: '1rem' }}>
      <SectionTitle
        actions={
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {firewall.enabled && (
              <ActionButton busyLabel="Syncing…" onClick={onSync}>Sync rules</ActionButton>
            )}
            <ActionButton busyLabel="Saving…" onClick={onSave}>Save firewall</ActionButton>
          </div>
        }
      >
        Host firewall (UFW)
      </SectionTitle>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <Pill tone={healthy ? 'emerald' : status.available ? 'muted' : 'red'}>
          {status.available ? (status.active ? 'active' : 'installed, inactive') : 'unavailable'}
        </Pill>
        <span className="mono muted" style={{ fontSize: '0.75rem' }}>{status.command}</span>
        {status.available && (
          <span className="muted" style={{ fontSize: '0.78rem' }}>
            {status.managedRules} hub-managed rule{status.managedRules === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {status.reason && (
        <div style={{ marginBottom: '0.75rem' }}>
          <Alert tone={status.available ? 'warn' : 'error'}>{status.reason}</Alert>
        </div>
      )}

      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Blocks always apply inside the hub. Turning this on additionally inserts a{' '}
        <span className="mono">deny</span> rule at the top of the UFW chain, so the traffic is dropped
        before it reaches the hub — and stays dropped while the hub is restarting. A firewall that is
        missing or unprivileged never fails a block; it is reported instead.
      </p>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
        <input
          type="checkbox"
          checked={firewall.enabled}
          onChange={(e) => onChange({ ...firewall, enabled: e.target.checked })}
        />
        <span style={{ color: 'var(--ha-text-primary)' }}>Push blocks to UFW</span>
      </label>

      {firewall.enabled && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))', gap: '0.75rem' }}>
          <Field
            label="Which blocks"
            help="Temporary blocks expire inside the hub on their own, so mirroring them churns firewall rules for addresses that were going to be let back in."
          >
            <select
              className="form-input"
              value={firewall.scope}
              onChange={(e) => onChange({ ...firewall, scope: e.target.value as FirewallSettings['scope'] })}
            >
              <option value="perm">Permanent blocks only</option>
              <option value="all">Every block, temporary included</option>
            </select>
          </Field>
          <Field label="On unblock" help="Remove the UFW rule when a block is lifted from this page.">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={firewall.removeOnUnblock}
                onChange={(e) => onChange({ ...firewall, removeOnUnblock: e.target.checked })}
              />
              <span className="muted">Delete the rule too</span>
            </label>
          </Field>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Trusted proxies
// ---------------------------------------------------------------------------

/**
 * Read-only: this is deployment configuration, not a runtime toggle, and it
 * would be the wrong thing to make changeable from a page that anyone who got
 * past the login can reach.
 */
function TrustProxyCard({ info }: { info: TrustProxyInfo }) {
  const proxied = info.yourAddress !== info.socketAddress;
  const looksUnset = !proxied && (info.socketAddress === '127.0.0.1' || info.socketAddress === '::1');

  return (
    <Card style={{ marginBottom: '1rem' }}>
      <SectionTitle>Client addresses</SectionTitle>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem' }}>
        <div>
          <div className="muted" style={{ fontSize: '0.72rem' }}>Trusted proxies</div>
          <div className="mono">{info.setting}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: '0.72rem' }}>Your address, as recorded</div>
          <div className="mono">{info.yourAddress}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: '0.72rem' }}>Socket peer</div>
          <div className="mono">{info.socketAddress}</div>
        </div>
      </div>

      {looksUnset && (
        <div style={{ marginTop: '0.75rem' }}>
          <Alert tone="warn">
            Every request is arriving from loopback. If a reverse proxy fronts this hub, it is not
            forwarding the client address, or <span className="mono">HUB_TRUST_PROXY</span> does not
            cover it — until that is fixed, every visitor counts as the same address and the
            per-IP detectors cannot tell them apart.
          </Alert>
        </div>
      )}

      <p className="muted" style={{ fontSize: '0.78rem', marginBottom: 0, marginTop: '0.75rem' }}>
        Set with <span className="mono">HUB_TRUST_PROXY</span>. Forwarded headers are honoured only
        from these hops — trusting everything would let any client name its own source address.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Blocks and events
// ---------------------------------------------------------------------------

function BlocksCard({ blocks, onUnblock }: {
  blocks: SecurityBlock[];
  onUnblock: (ip: string) => Promise<unknown>;
}) {
  return (
    <Card style={{ marginBottom: '1rem' }}>
      <SectionTitle>Active blocks</SectionTitle>
      {blocks.length === 0 ? (
        <p className="muted" style={{ fontSize: '0.85rem', margin: 0 }}>No addresses are currently blocked.</p>
      ) : (
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <th>Address</th><th>Kind</th><th>Reason</th><th>Blocked</th><th>Expires</th><th />
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => (
                <tr key={b.id}>
                  <td className="mono">{b.ip}</td>
                  <td><Pill tone={b.kind === 'perm' ? 'red' : 'muted'}>{b.kind}</Pill></td>
                  <td className="muted" style={{ fontSize: '0.8rem' }}>{b.reason}</td>
                  <td className="muted" style={{ fontSize: '0.8rem' }}>{fmtAgo(Math.floor(b.created_at / 1000))}</td>
                  <td className="muted" style={{ fontSize: '0.8rem' }}>
                    {b.expires_at ? fmtDate(b.expires_at) : 'never'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButton busyLabel="Lifting…" onClick={() => onUnblock(b.ip)}>Unblock</ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function EventsCard({ events }: { events: SecurityEvent[] }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <Card>
      <SectionTitle>Recent events</SectionTitle>
      {events.length === 0 ? (
        <p className="muted" style={{ fontSize: '0.85rem', margin: 0 }}>Nothing recorded yet.</p>
      ) : (
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <th>When</th><th>Alert</th><th>Summary</th><th>Address</th><th>Emailed</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpen((cur) => (cur === e.id ? null : e.id))}
                  >
                    <td className="muted" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {fmtAgo(Math.floor(e.created_at / 1000))}
                    </td>
                    <td className="mono" style={{ fontSize: '0.75rem' }}>{e.alert_key}</td>
                    <td style={{ fontSize: '0.82rem' }}>{e.summary}</td>
                    <td className="mono muted" style={{ fontSize: '0.78rem' }}>{e.ip ?? '-'}</td>
                    <td>
                      {e.emailed
                        ? <Pill tone="emerald">sent</Pill>
                        : e.email_error
                          ? <Pill tone="red" title={e.email_error}>failed</Pill>
                          : <Pill tone="muted">no</Pill>}
                    </td>
                  </tr>
                  {open === e.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--ha-bg-elevated)' }}>
                        <pre className="mono" style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                          {Object.entries(e.detail).map(([k, v]) => `${k}: ${v}`).join('\n') || '(no detail)'}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
